import { useSession } from 'components/auth/useSession'
import { useInscricoesTopicosNotificacaoQuery } from 'graphql/hooks.generated'
import { TipoTopicoNotificacaoEnum } from 'graphql/types.generated'
import { usePubSub } from 'hooks/pubsub/usePubSub'
import { isEqual } from 'lodash'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import {
  InscricoesTopicosNotificacaoContextModel,
  NotificacaoCountContextModel,
  NotificacaoPubSubResponseList,
} from './model-notificacaoContext'
import {
  applyAtmosphereMessages,
  createDefaultCountTipoNotificacaoRecord,
  createDefaultInscricoesTopicosNotificacaoRecord,
  mapQueryToInscricoes,
  recomputeCounts,
} from './util-notificacaoContext'

const NotificacaoCountContext = createContext<NotificacaoCountContextModel>(createDefaultCountTipoNotificacaoRecord())
const InscricoesTopicosNotificacaoContext = createContext<InscricoesTopicosNotificacaoContextModel>(
  createDefaultInscricoesTopicosNotificacaoRecord()
)

interface NotificacaoProviderProps {
  children: React.ReactNode
}

export function NotificacaoProvider(props: Readonly<NotificacaoProviderProps>) {
  const { children } = props
  const [inscricoesTopicos, setInscricoesTopicos] = useState(createDefaultInscricoesTopicosNotificacaoRecord())

  const { data: { acesso } = {} } = useSession()
  const tiposTopicosQuery = useMemo(() => Object.values(TipoTopicoNotificacaoEnum), [])
  const acessoId = acesso?.id

  const { data: { inscricoesTopicosNotificacao } = {} } = useInscricoesTopicosNotificacaoQuery({
    variables: { input: { acessoId: acessoId!, tiposTopicos: tiposTopicosQuery } },
    context: { newRequest: true },
    pollInterval: 180000, // 3 minutos
    fetchPolicy: 'network-only',
    skip: !acessoId,
  })

  useEffect(() => {
    if (!inscricoesTopicosNotificacao) return
    const next = mapQueryToInscricoes(inscricoesTopicosNotificacao)

    setInscricoesTopicos((estadoAnterior: InscricoesTopicosNotificacaoContextModel) => {
      return isEqual(next, estadoAnterior) ? estadoAnterior : next
    })
  }, [inscricoesTopicosNotificacao])

  const handleMessage = useCallback((mensagens: NotificacaoPubSubResponseList) => {
    if (!mensagens || mensagens.length === 0) return

    setInscricoesTopicos((estadoAnterior: InscricoesTopicosNotificacaoContextModel) => {
      const next = applyAtmosphereMessages(estadoAnterior, mensagens)
      return isEqual(next, estadoAnterior) ? estadoAnterior : next
    })
  }, [])

  usePubSub<NotificacaoPubSubResponseList>({
    topic: `/notificacao/${acessoId ?? ''}`,
    onMessage: handleMessage,
  })

  const notificacoes = useMemo(() => {
    if (!inscricoesTopicos) return createDefaultCountTipoNotificacaoRecord()
    return recomputeCounts(inscricoesTopicos)
  }, [inscricoesTopicos])

  return (
    <InscricoesTopicosNotificacaoContext.Provider value={inscricoesTopicos}>
      <NotificacaoCountContext.Provider value={notificacoes}>{children}</NotificacaoCountContext.Provider>
    </InscricoesTopicosNotificacaoContext.Provider>
  )
}

export const useInscricoesTopicosNotificacaoContext = () => useContext(InscricoesTopicosNotificacaoContext)
export const useNotificacaoCountContext = () => useContext(NotificacaoCountContext)
